import { useCallback } from 'react';
import type { AutomationStep, StepConnection } from '../types/automation';
import type { BranchContextEntry } from '../utils/flowGraphOps';

interface UseFlowVariableContextParams {
	steps: Array<AutomationStep>;
	connections: Array<StepConnection>;
	stepTypeSchemas: Map<string, Record<string, any>>;
	dataLoaderSchemas: Map<string, Record<string, any>>;
	agentOutputSchemas: Map<number, Record<string, any>>;
}

/**
 * The variable-resolution "crown jewel", extracted from `ListFlowBuilder`
 * (Phase 0 of the builder 2.0 work).
 *
 * Given the step the user is configuring, it walks every upstream step and
 * builds the set of variables that step can reference — previous-step
 * outputs, Start-node custom fields, system variables — applying the dynamic
 * schema rules for `wait_for_event`, `load_data` and `agent_call` steps.
 *
 * ## The branch-context reframe
 *
 * In 1.0 this function read the drill-in `branchPath` straight from
 * component state. The 2.0 builder drops drill-in for inline lanes, so the
 * branch context is now an **explicit parameter** of the returned function.
 * The host passes whichever context applies:
 *
 * - 1.0 `ListFlowBuilder` binds its drill-in `branchPath`.
 * - 2.0 builder binds whichever lane the step being configured sits in.
 *
 * Variable resolution itself is identical either way — only the *source* of
 * the branch context changed from implicit state to an explicit argument.
 */
export function useFlowVariableContext(
	params: UseFlowVariableContextParams,
): (
	stepIdentifier: string,
	branchContext: Array<BranchContextEntry>,
) => Record<string, any> {
	const {
		steps,
		connections,
		stepTypeSchemas,
		dataLoaderSchemas,
		agentOutputSchemas,
	} = params;

	return useCallback(
		(stepIdentifier: string, branchContext: Array<BranchContextEntry>) => {
			console.debug('Building step context for:', stepIdentifier);

			// stepIdentifier could be either internalId (UUID) or id (number
			// as string). Try to find step by internalId first, then by id.
			let targetStep = steps.find((s) => s.internalId === stepIdentifier);
			if (targetStep == null) {
				const parsedId = parseInt(stepIdentifier);
				if (!isNaN(parsedId)) {
					targetStep = steps.find((s) => s.id === parsedId);
				}
			}

			if (targetStep == null) {
				console.debug('Target step not found:', stepIdentifier);
				return { previousSteps: {}, systemVariables: {} };
			}

			const targetStepId = targetStep.id;

			// Find all steps that come before the target step
			const upstreamSteps = new Set<number>();
			const visited = new Set<number>();

			// Recursive function to traverse upstream connections
			const traverseUpstream = (currentStepId: number) => {
				if (visited.has(currentStepId)) {
					return;
				}

				visited.add(currentStepId);

				// Find all connections that lead TO the current step
				const incomingConnections = connections.filter(
					(c) => c.toStepId === currentStepId,
				);

				for (const conn of incomingConnections) {
					if (conn.fromStepId !== currentStepId) {
						upstreamSteps.add(conn.fromStepId);
						traverseUpstream(conn.fromStepId);
					}
				}
			};

			// If we're in a branch, also include upstream from the branch
			// parent. `branchContext` is passed explicitly by the host (see
			// the reframe note above) — empty array means main flow.
			if (branchContext.length > 0) {
				branchContext.forEach((branch) => {
					upstreamSteps.add(branch.stepId);
					traverseUpstream(branch.stepId);
				});
			}

			// Start traversal from target step
			traverseUpstream(targetStepId);

			// Build context from upstream steps
			const previousStepsContext: Record<string, any> = {};
			const startVariables: Record<string, any> = {};

			// Process each upstream step
			for (const upstreamStepId of upstreamSteps) {
				const step = steps.find((s) => s.id === upstreamStepId);

				if (step == null) {
					continue;
				}

				const stepType = step.stepType;
				const stepName = step.stepName;

				if (stepType === 'start') {
					// Handle Start node - extract custom fields
					const startConfig = step.config || {};

					if (
						(startConfig as any).inputType === 'custom' &&
						(startConfig as any).customFields != null
					) {
						for (const field of (startConfig as any).customFields) {
							startVariables[field.name] = {
								type: field.type,
								label: field.name,
								required: field.required,
								defaultValue: field.defaultValue,
								objectType: field.objectType,
								arrayType: field.arrayType,
							};
						}
					}
				} else if (stepType && stepName) {
					let outputSchema = stepTypeSchemas.get(stepType);

					// Special handling for wait_for_event - get schema from event type
					if (stepType === 'wait_for_event') {
						const stepConfig = step.config || {};
						const eventTypeKey = (stepConfig as any).eventType;

						// Note: We don't have eventTypes loaded here like in
						// FlowBuilder. The modal will need to handle this
						// separately or we need to load event types.
						if (eventTypeKey && (stepConfig as any).eventTypeSchema != null) {
							outputSchema = (stepConfig as any).eventTypeSchema;
						}
					}

					// Handle load_data step - output schema comes from data
					// loader registry based on dataType.
					if (stepType === 'load_data') {
						const stepConfig = step.config || {};
						const dataType = (stepConfig as any).dataType;
						console.debug('[buildStepContext] load_data step found:', {
							stepName,
							dataType,
							config: stepConfig,
							availableLoaders: Array.from(dataLoaderSchemas.keys()),
						});

						if (dataType != null && dataLoaderSchemas.has(dataType)) {
							outputSchema = dataLoaderSchemas.get(dataType);
						} else {
							// No dataType selected or unknown type - don't show
							// any outputs.
							outputSchema = undefined;
						}
					}

					// Handle agent_call step - merge the bound agent's declared
					// output variables on top of the static schema so the
					// picker shows `collectedVariables.<name>` entries for each
					// declared variable. Falls through to the static schema if
					// no agent is bound or the agent has no outputs declared.
					if (stepType === 'agent_call') {
						const stepConfig = step.config || {};
						const agentId = (stepConfig as any).agentId;
						if (
							typeof agentId === 'number' &&
							agentOutputSchemas.has(agentId)
						) {
							const agentEntries = agentOutputSchemas.get(agentId);
							if (
								agentEntries != null &&
								Object.keys(agentEntries).length > 0
							) {
								outputSchema = {
									...(outputSchema ?? {}),
									...agentEntries,
								};
							}
						}
					}

					if (outputSchema != null) {
						previousStepsContext[stepName] = {
							stepType,
							outputs: outputSchema,
						};
					}
				}
			}

			// System variables - always available
			const systemVariables = {
				'system.currentDate': { type: 'string', label: 'Current Date' },
				'system.currentTime': { type: 'string', label: 'Current Time' },
			};

			// Build the return context
			const context: Record<string, any> = {
				previousSteps: previousStepsContext,
				systemVariables,
			};

			// Add Start variables if available
			if (Object.keys(startVariables).length > 0) {
				context['Start'] = {
					stepType: 'start',
					outputs: startVariables,
				};
			}

			console.debug('Built context from list state:', context);
			return context;
		},
		[
			steps,
			connections,
			stepTypeSchemas,
			dataLoaderSchemas,
			agentOutputSchemas,
		],
	);
}
